import { useTranslations } from '@vocab/react';
import { Box, Divider, Stack, Text } from 'braid-design-system';
import { useMemo, type ComponentProps } from 'react';

import { GenAIChatLoadingOverlay } from '../LoadingOverlay';
import type { ErrorType } from '../types';

import translations from './.vocab';
import { TextSection } from './TextSection';

import * as styles from './CareerFeedAIAssistantContentFrame.css';

const ErrorMessage = ({ errorType }: { errorType: ErrorType }) => {
  const { t } = useTranslations(translations);

  const errorText = useMemo(() => {
    if (errorType === 'INPUT_OR_OUTPUT_BLOCKED') {
      return t('I can only help you with your job search.');
    } else if (errorType === 'TOO_MANY_REQUESTS') {
      return t('You’ve done this too many times today. Try again later.');
    }

    return t('Can we try that again? We’re having trouble connecting.');
  }, [errorType, t]);

  return <Text tone="critical">{errorText}</Text>;
};

const CareerFeedAIAssistantContent = ({
  headMessage,
  errorType,
  contentSection,
  socialProofSection,
  loading,
}: {
  headMessage:
    | {
        size?: ComponentProps<typeof TextSection>['size'];
        text: ComponentProps<typeof TextSection>['children'];
      }
    | string;
  contentSection?: ComponentProps<typeof Stack>['children'];
  socialProofSection?: ComponentProps<typeof Stack>['children'];
  ctaSection: ComponentProps<typeof Stack>['children'];
  errorType?: ComponentProps<typeof ErrorMessage>['errorType'] | null;
  loading: boolean;
}) => (
  <GenAIChatLoadingOverlay isGenerating={loading}>
    <Box paddingTop="xxsmall" paddingBottom="xsmall">
      <Stack space="medium">
        {typeof headMessage === 'string' ? (
          <TextSection>{headMessage}</TextSection>
        ) : (
          <TextSection size={headMessage.size}>{headMessage.text}</TextSection>
        )}
        {socialProofSection}
        {contentSection}
        {errorType ? <ErrorMessage errorType={errorType} /> : null}
      </Stack>
    </Box>
  </GenAIChatLoadingOverlay>
);

export const CareerFeedAIAssistantContentFrame = ({
  headMessage,
  errorType,
  contentSection,
  socialProofSection,
  ctaSection,
  loading,
}: {
  headMessage:
    | {
        size?: ComponentProps<typeof TextSection>['size'];
        text: ComponentProps<typeof TextSection>['children'];
      }
    | string;
  contentSection?: ComponentProps<typeof Stack>['children'];
  socialProofSection?: ComponentProps<typeof Stack>['children'];
  ctaSection: ComponentProps<typeof Stack>['children'];
  errorType?: ComponentProps<typeof ErrorMessage>['errorType'] | null;
  loading: boolean;
}) => (
  <Stack space="small">
    <CareerFeedAIAssistantContent
      headMessage={headMessage}
      errorType={errorType}
      contentSection={contentSection}
      socialProofSection={socialProofSection}
      ctaSection={ctaSection}
      loading={loading}
    />
    <Stack space="medium">{ctaSection}</Stack>
  </Stack>
);

export const CareerFeedAIAssistantContentFrameMobile = ({
  headMessage,
  errorType,
  contentSection,
  ctaSection,
  loading,
}: {
  headMessage:
    | {
        size?: ComponentProps<typeof TextSection>['size'];
        text: ComponentProps<typeof TextSection>['children'];
      }
    | string;
  contentSection?: ComponentProps<typeof Stack>['children'];
  ctaSection: ComponentProps<typeof Stack>['children'];
  errorType?: ComponentProps<typeof ErrorMessage>['errorType'] | null;
  loading: boolean;
}) => (
  <Box
    height="full"
    display="flex"
    flexDirection="column"
    className={styles.overflowY}
    justifyContent="spaceBetween"
  >
    <CareerFeedAIAssistantContent
      headMessage={headMessage}
      errorType={errorType}
      contentSection={contentSection}
      ctaSection={ctaSection}
      loading={loading}
    />

    <Box paddingTop="small">
      <Stack space="medium">
        <Divider />
        {ctaSection}
      </Stack>
    </Box>
  </Box>
);
